import { fuelTypeData, gearboxData } from '../../pages/resources/vehiclesModal/vehiclesModalData';
import { setLocalString } from '../../pages/intervals/intervalModal/utils/intervalSumHelper';
import { DebitType, DebitTypeTranslationForSum } from '../../types/DebitType';
import { toDateOnly } from '../../utils/dates';
import { formatPrice } from '../../utils/formatPrice';
import { getNestedValue } from '../../utils/getNestedValue';
import { SortValue, toSortValue } from '../../utils/sortValues';
import type { TableWidgetColumns } from './TableWidget';
import { Interval } from '../../models/Interval';
import { getIntervalFrequencyTitle } from '../../pages/intervals/intervalModal/utils/intervalFrequencyHelper';
import { formatFileSize } from '../../utils/formatFileSize';
import { formatClockTime } from '../../utils/formatClockTime';

const colorStatus = {
  exported: 'exported',
  needsReviewal: 'expired',
  locked: 'approved',
  lockedAndHasBeenEditedByAdmin: 'approved',
  hasBeenEditedByAdmin: 'expired',
  Avklarad: 'approved',
  Pågående: 'sent',
  Förfallen: 'expired',
  sent: 'sent',
  approved: 'approved',
  draft: 'draft',
  partiallyPaid: 'partial',
  rejected: 'rejected',
  expired: 'expired',
  removed: 'deleted',
  deleted: 'deleted',
  Skickad: 'sent',
  Godkänd: 'approved',
  Avvisad: 'rejected',
  Betald: 'approved',
  Utkast: 'draft',
  Makulerad: 'deleted',
  Utfärdad: 'sent',
  Åtgärdad: 'approved',
};

const intervalType = {
  weekly: 'Veckovis',
  monthly: 'Månadsvis',
  daily: 'Dagligen',
  callOff: 'Avrop',
};

const extraBillingType = {
  monthly: 'Månadspris',
  weekly: 'Veckopris',
  oneTime: 'Engångspris',
  ongoing: 'Löpande pris',
  unit: 'À-pris',
};

const serviceTypeTranslation = {
  st: 'Styck',
  tim: 'Timme',
};

const invoiceTypeTranslation = {
  perInstance: 'Per tillfälle',
  weekly: 'Veckovis',
  monthly: 'Månadsvis',
};

const weeksPerMonth = 52 / 12;

/**
 * Brings a weekly sum to a monthly one so that the two can be sorted against each other. Sums
 * per occasion, per hour or once have no period to convert, so they keep their own value.
 */
const toMonthlySum = (sum: unknown, debitType: unknown): unknown =>
  debitType === DebitType.Weekly && sum !== null && sum !== undefined && sum !== ''
    ? Number(sum) * weeksPerMonth
    : sum;

export interface FormattedTableRow {
  /** The text and values shown in the cells, which the search also matches. */
  row: Record<string, unknown>;
  /** The value each column sorts by, so formatted amounts and dates sort by what they mean. */
  sortValues: Record<string, SortValue>;
}

/**
 * Turns one data item into the values TableWidget shows and sorts by, column by column.
 */
export const formatTableRow = (
  item: Record<string, unknown>,
  columns: TableWidgetColumns[]
): FormattedTableRow => {
  const row: Record<string, unknown> = {};
  const sortValues: Record<string, SortValue> = {};

  columns.forEach(({ id, path, formatter }) => {
    const raw = item[id];
    const show = (display: unknown, sortBy: unknown = display) => {
      row[id] = display;
      sortValues[id] = toSortValue(sortBy);
    };

    switch (id) {
      case 'fullName':
        show(`${item['firstName']} ${item['lastName']}`);
        break;
      case 'extent':
      case 'discount':
        show(`${raw || 0}%`, raw || 0);
        break;
      case 'price':
      case 'listPrice':
      case 'balance':
      case 'totalSale':
      case 'scheduledTaskSum':
      case 'unpaidAmount':
      case 'total':
      case 'calculationCost':
        show(formatPrice({ countryCode: 'sv-SE', currency: 'SEK', value: raw as string }), raw);
        break;
      case 'fuelType':
        show(fuelTypeData.find((fuelType) => fuelType.value === raw)?.title ?? raw);
        break;
      case 'gearbox':
        show(gearboxData.find((gearbox) => gearbox.value === raw)?.title ?? raw);
        break;
      case 'brandModel':
        show(`${item['brand']} ${item['model']}`);
        break;
      case 'colorStatus':
        show(item['statusInSwedish'] ?? '');
        row['colorStatusClass'] = colorStatus[raw as keyof typeof colorStatus] || 'draft';
        break;
      case 'startTime':
      case 'endTime':
        show(formatClockTime(raw as string));
        break;
      case 'startDate':
      case 'updated':
      case 'createdAt':
      case 'date':
      case 'endDate':
        if (!raw) {
          show('-', null);
        } else {
          show(toDateOnly(raw as string), raw);
        }
        break;
      case 'createdOn':
        show(toDateOnly(raw as string), raw);
        break;
      case 'occasionCount': {
        const occasionCount = (item['occasions'] as unknown[]).length;
        show(`${occasionCount}`, occasionCount);
        break;
      }
      // A one-time interval is stored as daily or weekly with frequency 0, so its debit type is
      // what tells it apart.
      case 'intervalType':
        show(
          item['debitType'] === DebitType.OneTime
            ? 'En gång'
            : intervalType[raw as keyof typeof intervalType]
        );
        break;
      case 'frequency':
        // A week-parity interval has no frequency; show "Jämna veckor"/"Ojämna veckor" instead.
        if (item['debitType'] === DebitType.OneTime) {
          show('En gång', raw);
        } else if (item['weekParity']) {
          show(getIntervalFrequencyTitle(item as unknown as Interval), raw);
        } else {
          show(raw);
        }
        break;
      case 'priceType':
      case 'debitType':
        show(extraBillingType[raw as keyof typeof extraBillingType]);
        break;
      case 'sumOfYieldOfPeriod': {
        const debitType = item['debitType'];
        show(
          `${setLocalString(raw as string)} kr/${DebitTypeTranslationForSum[debitType as DebitType]}`,
          toMonthlySum(raw, debitType)
        );
        break;
      }
      case 'orderValue':
        show(`${setLocalString(raw as string)} kr`, raw);
        break;
      case 'fileSize':
        show(formatFileSize(raw as number), raw || null);
        break;
      case 'invoiceType':
        show(invoiceTypeTranslation[raw as keyof typeof invoiceTypeTranslation]);
        break;
      case 'serviceType':
        show(serviceTypeTranslation[raw as keyof typeof serviceTypeTranslation]);
        break;
      default: {
        const value = getNestedValue(item, path ?? id);
        show(formatter ? formatter(value) : value, value);
        break;
      }
    }
  });

  return { row, sortValues };
};
